import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import {
  listShares,
  revokeShare,
  resendShare,
  type OpsPlanShareSummary,
} from '@/services/opsPlanShares.service';

interface SharesPanelProps {
  activityId: string;
}

type ShareStatus = 'Revoked' | 'Locked' | 'Expired' | 'Active';

function getShareStatus(share: OpsPlanShareSummary): ShareStatus {
  if (share.revokedAt) { return 'Revoked'; }
  if (share.lockedAt) { return 'Locked'; }
  if (share.expiresAt && new Date(share.expiresAt) <= new Date()) { return 'Expired'; }
  return 'Active';
}

const STATUS_BADGE: Record<ShareStatus, string> = {
  Revoked: 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400',
  Locked: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  Expired: 'bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400',
  Active: 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function SharesPanel({ activityId }: SharesPanelProps) {
  const queryClient = useQueryClient();
  const [revokeTargetId, setRevokeTargetId] = useState<string | null>(null);

  const { data: shares, isLoading } = useQuery({
    queryKey: ['ops-plan-shares', activityId],
    queryFn: () => listShares(activityId),
  });

  const resendMutation = useMutation({
    mutationFn: (shareId: string) => resendShare(activityId, shareId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ops-plan-shares', activityId] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (shareId: string) => revokeShare(activityId, shareId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ops-plan-shares', activityId] });
      setRevokeTargetId(null);
    },
  });

  const handleRevokeConfirm = () => {
    if (revokeTargetId) {
      revokeMutation.mutate(revokeTargetId);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>External Shares</CardTitle>
          <CardDescription>
            Everyone this ops plan has been shared with. Revoking ends access immediately.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              Loading shares...
            </p>
          )}

          {!isLoading && (!shares || shares.length === 0) && (
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              This ops plan has not been shared yet.
            </p>
          )}

          {!isLoading && shares && shares.length > 0 && (
            <ul className="divide-y divide-secondary-200 dark:divide-secondary-700">
              {shares.map((share) => {
                const status = getShareStatus(share);
                const isActionable = status === 'Active' || status === 'Locked';

                return (
                  <li
                    key={share.id}
                    data-testid="share-row"
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-secondary-900 dark:text-white truncate">
                          {share.recipientName}
                        </span>
                        <span className="text-sm text-secondary-500 dark:text-secondary-400 truncate">
                          {share.recipientEmail}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[status]}`}
                        >
                          {status}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-secondary-500 dark:text-secondary-400">
                        {share.recipientType === 'CONTRACTOR' ? 'Contractor' : 'Executive'}
                        {' · expires '}
                        {formatDate(share.expiresAt)}
                        {' · viewed '}
                        {share.viewCount} time{share.viewCount !== 1 ? 's' : ''}
                        {share.lastViewedAt ? ` (last ${formatDate(share.lastViewedAt)})` : ''}
                      </p>
                    </div>

                    {isActionable && (
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resendMutation.mutate(share.id)}
                          disabled={resendMutation.isPending && resendMutation.variables === share.id}
                          loading={resendMutation.isPending && resendMutation.variables === share.id}
                        >
                          Resend
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setRevokeTargetId(share.id)}
                          disabled={revokeMutation.isPending && revokeTargetId === share.id}
                        >
                          Revoke
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <ConfirmationDialog
        isOpen={revokeTargetId !== null}
        onClose={() => setRevokeTargetId(null)}
        onConfirm={handleRevokeConfirm}
        title="Revoke share"
        message="Are you sure you want to revoke this share? The recipient will immediately lose access to the operations plan."
        confirmText="Revoke"
        cancelText="Cancel"
        variant="danger"
        isLoading={revokeMutation.isPending}
      />
    </>
  );
}
